// types
import { TraceabilityType, TraceabilityTransactions } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// ui
import { CardKeyStatProps, CardKeyStats, CardKeyStatsProps } from '../CardKeyStats/CardKeyStats';
import { CardSeverity, CardSeverityProps } from '../CardSeverity/CardSeverity';

// dialogs
import { DialogTraceabilityProps } from '../../dialogs/DialogTraceability/DialogTraceability';

// utils
import { formatNumber, formatPercentage } from '../../../utils/format';

// config
import { featureFlags } from '../../../config/featureFlags';
import { formatCurrency } from '../../../utils/currency';

// props
export interface CardProps {
	ChipStatusProps: CardSeverityProps['ChipStatusProps'];
	DialogProps: CardKeyStatsProps['DialogProps'];
	footnote: CardKeyStatProps['footnote'];
	label: CardSeverityProps['label'];
	lineItems: CardKeyStatProps['lineItems'];
	severity: CardSeverityProps['severity'];
	title: CardKeyStatProps['title'];
	value: CardKeyStatProps['value'];
}

export interface CardTraceabilityProps {
	card?: 'KEY_STATS' | 'SEVERITY';
	DialogProps?: Partial<DialogTraceabilityProps>;
	isLoading?: boolean;
	traceability: TraceabilityTransactions;
	type?: TraceabilityType;
}

export const CardTraceability = ({
	card = 'KEY_STATS',
	DialogProps,
	isLoading = false,
	traceability,
	type,
}: CardTraceabilityProps) => {
	// hooks
	const { t } = useTranslation();

	// vars
	const isTransactions = type === 'TRANSACTIONS';
	const isFinancial = type === 'FINANCIAL';

	let severity: CardSeverityProps['severity'] = 'default';

	// Skip financial traceability display if feature flag is disabled
	if (type === 'FINANCIAL' && !featureFlags['FINANCIAL_TRACEABILITY']) {
		return <></>;
	}

	const getChipStatusProps = ({ value }: { value: null | number }) => {
		const ChipStatusProps: CardSeverityProps['ChipStatusProps'] = {
			status: 'PENDING',
		};

		if (typeof value === 'number') {
			if (value <= 49) {
				ChipStatusProps.status = 'LOW';
				severity = 'error';
			} else if (value >= 50 && value <= 70) {
				ChipStatusProps.status = 'MINIMAL';
				severity = 'warning';
			} else if (value >= 71) {
				ChipStatusProps.status = 'HIGH';
				severity = 'success';
			}
		}

		return { severity, ChipStatusProps };
	};

	const {
		ChipStatusProps: transactionsEvidencedChipStatusProps,
		severity: transactionsEvidencedSeverity,
	} = getChipStatusProps({ value: traceability?.documentation?.value });
	const {
		ChipStatusProps: financialValueEvidencedChipStatusProps,
		severity: financialValueEvidencedSeverity,
	} = getChipStatusProps({ value: traceability?.financial?.value });

	const propsCards: Record<string, CardProps> = {
		financialValueEvidenced: {
			ChipStatusProps: financialValueEvidencedChipStatusProps,
			DialogProps: {
				...DialogProps,
				type: 'FINANCIAL',
				traceability,
			},
			footnote: {
				children: t('financialValueEvidenced.footnote'),
			},
			label: {
				children: t('financialValueEvidenced'),
			},
			lineItems: [
				{
					label: t('xOfYEvidenced', {
						x: 0,
						y: 0,
					}),
					value: formatPercentage(0),
				},
			],
			severity: financialValueEvidencedSeverity,
			title: {
				children: t('financialValueEvidenced'),
			},
			value: formatPercentage(traceability?.financial?.value || 0),
		},
		transactionsEvidenced: {
			ChipStatusProps: transactionsEvidencedChipStatusProps,
			DialogProps: {
				...DialogProps,
				type: 'TRANSACTIONS',
				traceability,
			},
			footnote: {
				children: t('transactionsEvidenced.footnote'),
			},
			label: {
				children: t('transactionsEvidenced'),
			},
			lineItems: [
				{
					label: t('fullyEvidenced'),
					value: formatNumber(Number(traceability?.documentation?.documented || 0), {
						notation: 'compact',
					}),
				},
				{
					label: t('partiallyEvidenced'),
					value: formatNumber(Number(traceability?.documentation?.partial || 0), {
						notation: 'compact',
					}),
				},
				{
					label: t('notEvidenced'),
					value: formatNumber(Number(traceability?.documentation?.undocumented || 0), {
						notation: 'compact',
					}),
				},
			],
			severity: transactionsEvidencedSeverity,
			title: {
				children: t('transactionsEvidenced'),
			},
			value: formatPercentage(traceability?.documentation?.value || 0),
		},
	};

	if (traceability?.financial) {
		propsCards.financialValueEvidenced.lineItems = traceability?.financial.currencies.map((c) => {
			return {
				label: t('xOfYEvidenced', {
					x: formatCurrency({
						currency: c.currency,
						notation: 'compact',
						value: c.documented || 0,
					}),
					y: formatCurrency({ currency: c.currency, notation: 'compact', value: c.total || 0 }),
				}),
				value: formatPercentage(c.value || 0),
			};
		});
	}

	// define default values
	let ChipStatusProps: CardProps['ChipStatusProps'] =
		propsCards.transactionsEvidenced.ChipStatusProps;
	let propsDialog: CardProps['DialogProps'] = propsCards.transactionsEvidenced.DialogProps;
	let footnote: CardProps['footnote'] = propsCards.transactionsEvidenced.footnote;
	let label: CardProps['label'] = propsCards.transactionsEvidenced.label;
	let lineItems: CardProps['lineItems'] = propsCards.transactionsEvidenced.lineItems;
	let value: CardProps['value'] = formatPercentage(0);

	if (isTransactions) {
		value = propsCards.transactionsEvidenced.value;
	}

	// override with financial values if type is financial
	if (isFinancial && featureFlags['FINANCIAL_TRACEABILITY']) {
		ChipStatusProps = propsCards.financialValueEvidenced.ChipStatusProps;
		propsDialog = propsCards.financialValueEvidenced.DialogProps;
		footnote = propsCards.financialValueEvidenced.footnote;
		label = propsCards.financialValueEvidenced.label;
		lineItems = propsCards.financialValueEvidenced.lineItems;
		value = propsCards.financialValueEvidenced.value;
	}

	const idDialog = type ? `TRACEABILITY_${type}` : undefined;
	const stats = [];

	if (card === 'KEY_STATS') {
		if (type) {
			if (isFinancial) {
				stats.push({ ...propsCards.financialValueEvidenced });
			} else if (isTransactions) {
				stats.push({ ...propsCards.transactionsEvidenced });
			}
		} else {
			// show both if no type specified
			stats.push({ ...propsCards.transactionsEvidenced });

			if (featureFlags['FINANCIAL_TRACEABILITY']) {
				stats.push({ ...propsCards.financialValueEvidenced });
			}
		}

		return (
			<CardKeyStats
				dialog="TRACEABILITY"
				DialogProps={{
					...propsDialog,
					id: idDialog,
				}}
				isLoading={isLoading}
				stats={stats}
			/>
		);
	}

	return (
		<CardSeverity
			ChipStatusProps={ChipStatusProps}
			dialog="TRACEABILITY"
			DialogProps={{
				...propsDialog,
				id: idDialog,
			}}
			footnote={footnote?.children as string}
			isLoading={isLoading}
			label={label}
			lineItems={lineItems}
			severity={severity}
			value={{
				children: value,
			}}
		/>
	);
};
